import {useLocalStorage} from 'platform/components';

import {useEffect, useReducer} from 'react';

import {isEmpty, isNotNil} from 'ramda';

import {Nullish, useQueryState} from 'shared/web';

import {AUCTION_DEFAULT_ORDER_ID} from '../../../constants/AUCTION_DEFAULT_ORDER_ID';
import {CLASSIFIEDS_DEFAULT_ORDER_ID} from '../../../constants/CLASSIFIEDS_DEFAULT_ORDER_ID';
import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';
import {useSourcingTargetCountry} from '../../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../../hooks/useSourcingType';
import {useFilters} from '../../../pages/AllOffers/components/FiltersContext/FiltersContext';
import {reducer, SourcingTableDispatch} from '../../../store/sourcingTable/reducer';
import {SourcingTableName} from '../types/SourcingTableName';
import {SourcingTableState} from '../types/SourcingTableState';
import {sourcingTableSessionStorage} from '../utils/sourcingTableSessionStorage';

export function useSourcingTable(
  tableName: SourcingTableName,
  initialOrderBy: string,
  selectedFilterPresetId?: string | Nullish
): [SourcingTableState, SourcingTableDispatch] {
  const [queryFilterPresetId] = useQueryState('filterPresetId');
  const [orderByFromLocalStorage, saveOrderByToLocalStorage] = useLocalStorage(
    `sourcing-table--sorting/${tableName}`,
    initialOrderBy
  );
  const sourcingType = useSourcingType();

  const [state, dispatch] = useReducer(reducer, {
    tableName,
    orderBy: isEmpty(orderByFromLocalStorage) ? initialOrderBy : orderByFromLocalStorage,
    page: null,
    checkedRows: [],
    selectedRow: null,
    expandedRowIds: [],
    isRealTime: sourcingTableSessionStorage.getIsRealTime(),
  });

  const {filters} = useFilters();
  const currency = useSourcingCurrency();
  const targetCountry = useSourcingTargetCountry();

  useEffect(() => {
    // Query parameter is added when clicking on notification; handled in useSourcingFilterPresets()
    if (isNotNil(queryFilterPresetId)) {
      dispatch({
        type: 'sort',
        column:
          sourcingType === SourcingType.AUCTIONS
            ? AUCTION_DEFAULT_ORDER_ID
            : CLASSIFIEDS_DEFAULT_ORDER_ID,
      });
    }
  }, [queryFilterPresetId, sourcingType]);

  useEffect(() => {
    // Reset page to 1 when filters or filter preset change
    dispatch({type: 'changePage', page: 1});
  }, [dispatch, filters, selectedFilterPresetId, currency?.code, targetCountry?.code]);

  useEffect(() => {
    saveOrderByToLocalStorage(state.orderBy);
  }, [state.orderBy, saveOrderByToLocalStorage]);

  useEffect(() => {
    sourcingTableSessionStorage.setLastVisitedPage(tableName, state.page ?? 1);
  }, [state.page, tableName]);

  useEffect(() => {
    sourcingTableSessionStorage.setIsRealTime(state.isRealTime);
  }, [state.isRealTime]);

  return [state, dispatch];
}
